import { Link } from 'react-router'
import { useRef, type ReactNode, useEffect, useState } from 'react'
import clsx from 'clsx'
import { Wordmark } from '~/components'

type Props = {
  className?: string
  children?: ReactNode
  onClickWordmark?: () => void
}

export const Header = ({ className, children, onClickWordmark }: Props) => {
  const ref = useRef<HTMLElement>(null)
  const [stuck, setStuck] = useState(false)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    let footer = document.querySelector('footer')
    let animationFrame = 0

    const getFlash = () => {
      const flash = el.previousElementSibling
      return flash instanceof HTMLElement && flash.classList.contains('flash')
        ? flash
        : null
    }

    const syncHeaderHeight = () => {
      if (animationFrame) window.cancelAnimationFrame(animationFrame)

      animationFrame = window.requestAnimationFrame(() => {
        const gap = parseFloat(getComputedStyle(el).paddingTop) || 0
        const offset = Math.min(window.scrollY, gap)
        const visibleHeaderGap = Math.max(0, gap - offset)
        const headerContent = el.querySelector<HTMLElement>('.header-content')
        if (!headerContent) return

        const flashHeight = getFlash()?.getBoundingClientRect().height ?? 0
        const visibleHeaderGapAfterFlash = Math.max(
          0,
          visibleHeaderGap - flashHeight,
        )
        const headerContentStyle = getComputedStyle(headerContent)
        const verticalChrome =
          (parseFloat(headerContentStyle.paddingTop) || 0) +
          (parseFloat(headerContentStyle.paddingBottom) || 0) +
          (parseFloat(headerContentStyle.borderTopWidth) || 0) +
          (parseFloat(headerContentStyle.borderBottomWidth) || 0)
        const persistentHeaderItems = [
          headerContent.querySelector<HTMLElement>('.logo'),
          headerContent.querySelector<HTMLElement>('.nav-row'),
          headerContent.querySelector<HTMLElement>('.header-actions-mobile'),
        ]
        const persistentHeaderContentHeight = persistentHeaderItems.reduce(
          (height, item) => {
            if (!item || item.offsetParent === null) return height
            return Math.max(height, item.getBoundingClientRect().height)
          },
          0,
        )
        const visibleHeight =
          visibleHeaderGapAfterFlash +
          verticalChrome +
          persistentHeaderContentHeight

        document.documentElement.style.setProperty(
          '--header-height',
          `${visibleHeight}px`,
        )
      })
    }

    const handleScroll = () => {
      const gap = parseFloat(getComputedStyle(el).paddingTop) || 0
      const offset = Math.min(window.scrollY, gap)

      if (!footer) footer = document.querySelector('footer')
      let push = 0
      if (footer) {
        const gapBeforeFooter = 24
        const headerBottom = el.offsetHeight - offset
        const footerTop = footer.getBoundingClientRect().top
        push = Math.max(0, headerBottom - footerTop + gapBeforeFooter)
      }

      const shift = offset + push
      el.style.transform = shift ? `translateY(${-shift}px)` : ''
      setStuck(gap > 0 ? window.scrollY >= gap : window.scrollY > 0)
      syncHeaderHeight()
    }

    const observer = new ResizeObserver(syncHeaderHeight)
    observer.observe(el)
    const flash = getFlash()
    if (flash) observer.observe(flash)

    const parentObserver = new MutationObserver(() => {
      const flash = getFlash()
      if (flash) observer.observe(flash)
      syncHeaderHeight()
    })
    if (el.parentElement) {
      parentObserver.observe(el.parentElement, { childList: true })
    }

    handleScroll()
    window.addEventListener('scroll', handleScroll, { passive: true })
    window.addEventListener('resize', syncHeaderHeight)

    return () => {
      observer.disconnect()
      parentObserver.disconnect()
      window.removeEventListener('scroll', handleScroll)
      window.removeEventListener('resize', syncHeaderHeight)
      if (animationFrame) window.cancelAnimationFrame(animationFrame)
    }
  }, [])

  return (
    <header
      ref={ref}
      className={clsx('fixed-header app', className, stuck && 'stuck')}
    >
      <section className="header-content">
        <Link to={'/'} className="logo" onClick={onClickWordmark}>
          <Wordmark />
        </Link>
        {children}
      </section>
    </header>
  )
}
